import { supabase } from '../lib/supabase';

// ============================================================
// USER PROFILE SERVICE
// ============================================================
export const userProfileService = {
  async getProfile(userId) {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
    if (error) throw error;
    return {
      id: data?.id,
      email: data?.email,
      fullName: data?.full_name,
      phone: data?.phone,
      avatarUrl: data?.avatar_url,
      role: data?.role,
      isActive: data?.is_active,
      createdAt: data?.created_at,
    };
  },

  async updateProfile(userId, updates) {
    const updatePayload = {
      full_name: updates?.fullName,
      phone: updates?.phone,
      avatar_url: updates?.avatarUrl,
    };
    if (updates?.themePreference !== undefined) {
      updatePayload.theme_preference = updates?.themePreference;
    }
    const { data, error } = await supabase?.from('user_profiles')?.update(updatePayload)?.eq('id', userId)?.select()?.single();
    if (error) throw error;
    return data;
  },

  async getAllUsers() {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
    if (error) throw error;
    return data?.map(u => ({
      id: u?.id,
      email: u?.email,
      fullName: u?.full_name,
      phone: u?.phone,
      role: u?.role,
      isActive: u?.is_active,
      createdAt: u?.created_at,
    })) || [];
  },
};

// ============================================================
// DRIVER PROFILE SERVICE
// ============================================================
export const driverService = {
  async getDriverProfile(userId) {
    const { data, error } = await supabase?.from('driver_profiles')?.select('*, user_profiles(full_name, email, phone, avatar_url)')?.eq('user_id', userId)?.maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return {
      id: data?.id,
      userId: data?.user_id,
      vehicleType: data?.vehicle_type,
      vehicleName: data?.vehicle_name,
      vehicleColor: data?.vehicle_color,
      vehicleYear: data?.vehicle_year,
      licensePlate: data?.license_plate,
      status: data?.status,
      rating: data?.rating,
      totalTrips: data?.total_trips,
      acceptanceRate: data?.acceptance_rate,
      completionRate: data?.completion_rate,
      walletBalance: data?.wallet_balance,
      referralCode: data?.referral_code,
      referralCount: data?.referral_count,
      referralEarnings: data?.referral_earnings,
      isVerified: data?.is_verified,
      name: data?.user_profiles?.full_name,
      email: data?.user_profiles?.email,
      phone: data?.user_profiles?.phone,
    };
  },

  async updateDriverStatus(driverId, status) {
    const { data, error } = await supabase?.from('driver_profiles')?.update({ status })?.eq('id', driverId)?.select()?.single();
    if (error) throw error;
    return data;
  },

  async updateDriverLocation(driverId, orderId, latitude, longitude, heading, speed) {
    const { error } = await supabase?.from('driver_locations')?.upsert(
      {
        driver_id: driverId,
        order_id: orderId,
        latitude,
        longitude,
        heading,
        speed,
        updated_at: new Date()?.toISOString(),
      },
      { onConflict: 'driver_id' }
    );
    if (error) throw error;
  },

  async broadcastDriverLocation(driverId, orderId, latitude, longitude, heading, speed) {
    // Upsert by driver_id to keep only the latest position row per driver
    const { error } = await supabase?.from('driver_locations')?.upsert(
      {
        driver_id: driverId,
        order_id: orderId ?? null,
        latitude,
        longitude,
        heading: heading ?? null,
        speed: speed ?? null,
        recorded_at: new Date()?.toISOString(),
      },
      { onConflict: 'driver_id', ignoreDuplicates: false }
    );
    if (error) {
      // Fallback to insert if upsert fails (no unique constraint on driver_id)
      const { error: insertError } = await supabase?.from('driver_locations')?.insert({
        driver_id: driverId,
        order_id: orderId ?? null,
        latitude,
        longitude,
        heading: heading ?? null,
        speed: speed ?? null,
      });
      if (insertError) throw insertError;
    }
  },

  async getLatestDriverLocation(driverId) {
    const { data, error } = await supabase?.from('driver_locations')?.select('*')?.eq('driver_id', driverId)?.order('recorded_at', { ascending: false })?.limit(1)?.maybeSingle();
    if (error) throw error;
    return data;
  },

  async getAllDrivers() {
    const { data, error } = await supabase?.from('driver_profiles')?.select('*, user_profiles(full_name, email, phone)')?.order('created_at', { ascending: false });
    if (error) throw error;
    return data?.map(d => ({
      id: d?.id,
      userId: d?.user_id,
      name: d?.user_profiles?.full_name,
      email: d?.user_profiles?.email,
      vehicleType: d?.vehicle_type,
      licensePlate: d?.license_plate,
      status: d?.status,
      rating: d?.rating,
      totalTrips: d?.total_trips,
      walletBalance: d?.wallet_balance,
      isVerified: d?.is_verified,
    })) || [];
  },
};

// ============================================================
// ORDER SERVICE
// ============================================================
export const orderService = {
  async createOrder(customerId, orderData) {
    const orderNumber = 'OTG-' + new Date()?.getFullYear() + '-' + Math.floor(Math.random() * 9000 + 1000);
    const { data, error } = await supabase?.from('orders')?.insert({
        order_number: orderNumber,
        customer_id: customerId,
        service_type: orderData?.serviceType,
        vehicle_type: orderData?.vehicleType,
        pickup_address: orderData?.pickupAddress,
        pickup_lat: orderData?.pickupLat,
        pickup_lng: orderData?.pickupLng,
        dropoff_address: orderData?.dropoffAddress,
        dropoff_lat: orderData?.dropoffLat,
        dropoff_lng: orderData?.dropoffLng,
        distance_km: orderData?.distanceKm,
        fare_amount: orderData?.fareAmount,
        platform_fee: orderData?.platformFee,
        total_amount: orderData?.totalAmount,
        scheduled_at: orderData?.scheduledAt,
        notes: orderData?.notes,
        delivery_details: orderData?.deliveryDetails ?? null,
      })?.select()?.single();
    if (error) throw error;
    return orderService?._mapOrder(data);
  },

  async getOrder(orderId) {
    const { data, error } = await supabase?.from('orders')?.select(`
        *,
        customer:user_profiles!orders_customer_id_fkey(full_name, email, phone),
        driver:driver_profiles(
          vehicle_name, vehicle_color, license_plate, rating, total_trips,
          user_profiles(full_name, phone)
        ),
        order_stops(*)
      `)?.eq('id', orderId)?.single();
    if (error) throw error;
    return orderService?._mapOrderWithRelations(data);
  },

  async getCustomerOrders(customerId) {
    const { data, error } = await supabase?.from('orders')?.select(`
        *,
        driver:driver_profiles(
          vehicle_name, license_plate, rating,
          user_profiles(full_name, phone)
        )
      `)?.eq('customer_id', customerId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data?.map(orderService?._mapOrder) || [];
  },

  async getDriverOrders(driverProfileId) {
    const { data, error } = await supabase?.from('orders')?.select(`
        *,
        customer:user_profiles!orders_customer_id_fkey(full_name, phone),
        order_stops(*)
      `)?.eq('driver_id', driverProfileId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data?.map(orderService?._mapOrder) || [];
  },

  async getAllOrders(filters = {}) {
    let query = supabase?.from('orders')?.select(`
        *,
        customer:user_profiles!orders_customer_id_fkey(full_name, email),
        driver:driver_profiles(license_plate, user_profiles(full_name))
      `)?.order('created_at', { ascending: false });

    if (filters?.status) query = query?.eq('status', filters?.status);
    if (filters?.serviceType) query = query?.eq('service_type', filters?.serviceType);
    if (filters?.limit) query = query?.limit(filters?.limit);

    const { data, error } = await query;
    if (error) throw error;
    return data?.map(orderService?._mapOrder) || [];
  },

  async updateOrderStatus(orderId, status, extraFields = {}) {
    const updates = { status, ...extraFields };
    const statusTimestamps = {
      driver_assigned: { accepted_at: new Date()?.toISOString() },
      picked_up: { picked_up_at: new Date()?.toISOString() },
      delivered: { delivered_at: new Date()?.toISOString() },
      cancelled: { cancelled_at: new Date()?.toISOString() },
    };
    if (statusTimestamps?.[status]) {
      Object.assign(updates, statusTimestamps?.[status]);
    }
    const { data, error } = await supabase?.from('orders')?.update(updates)?.eq('id', orderId)?.select()?.single();
    if (error) throw error;
    return orderService?._mapOrder(data);
  },

  async cancelOrder(orderId, reason) {
    return orderService?.updateOrderStatus(orderId, 'cancelled', { cancel_reason: reason });
  },

  async getOrderStops(orderId) {
    const { data, error } = await supabase?.from('order_stops')?.select('*')?.eq('order_id', orderId)?.order('stop_index', { ascending: true });
    if (error) throw error;
    return data?.map(s => ({
      id: s?.id,
      orderId: s?.order_id,
      stopIndex: s?.stop_index,
      address: s?.address,
      recipient: s?.recipient_name,
      recipientPhone: s?.recipient_phone,
      note: s?.notes,
      status: s?.status,
      completedAt: s?.completed_at,
      eta: s?.eta_minutes,
    })) || [];
  },

  async updateStopStatus(stopId, status) {
    const updates = { status };
    if (status === 'completed') updates.completed_at = new Date()?.toISOString();
    const { data, error } = await supabase?.from('order_stops')?.update(updates)?.eq('id', stopId)?.select()?.single();
    if (error) throw error;
    return data;
  },

  _mapOrder(o) {
    return {
      id: o?.id,
      orderNumber: o?.order_number,
      customerId: o?.customer_id,
      driverId: o?.driver_id,
      serviceType: o?.service_type,
      vehicleType: o?.vehicle_type,
      status: o?.status,
      pickupAddress: o?.pickup_address,
      pickupLat: o?.pickup_lat ? parseFloat(o?.pickup_lat) : null,
      pickupLng: o?.pickup_lng ? parseFloat(o?.pickup_lng) : null,
      dropoffAddress: o?.dropoff_address,
      dropoffLat: o?.dropoff_lat ? parseFloat(o?.dropoff_lat) : null,
      dropoffLng: o?.dropoff_lng ? parseFloat(o?.dropoff_lng) : null,
      distanceKm: o?.distance_km,
      fareAmount: o?.fare_amount,
      platformFee: o?.platform_fee,
      totalAmount: o?.total_amount,
      scheduledAt: o?.scheduled_at,
      acceptedAt: o?.accepted_at,
      pickedUpAt: o?.picked_up_at,
      deliveredAt: o?.delivered_at,
      cancelledAt: o?.cancelled_at,
      cancelReason: o?.cancel_reason,
      notes: o?.notes,
      deliveryDetails: o?.delivery_details ?? null,
      createdAt: o?.created_at,
      customer: o?.customer ? { fullName: o?.customer?.full_name, email: o?.customer?.email, phone: o?.customer?.phone } : null,
      driver: o?.driver ? {
        vehicleName: o?.driver?.vehicle_name,
        vehicleColor: o?.driver?.vehicle_color,
        licensePlate: o?.driver?.license_plate,
        rating: o?.driver?.rating,
        totalTrips: o?.driver?.total_trips,
        name: o?.driver?.user_profiles?.full_name,
        phone: o?.driver?.user_profiles?.phone,
      } : null,
    };
  },

  _mapOrderWithRelations(o) {
    const mapped = orderService?._mapOrder(o);
    mapped.stops = o?.order_stops?.map(s => ({
      id: s?.id,
      stopIndex: s?.stop_index,
      address: s?.address,
      recipient: s?.recipient_name,
      note: s?.notes,
      status: s?.status,
      completedAt: s?.completed_at,
      eta: s?.eta_minutes,
    })) || [];
    return mapped;
  },
};

// ============================================================
// TRANSACTION SERVICE
// ============================================================
export const transactionService = {
  async getUserTransactions(userId) {
    const { data, error } = await supabase?.from('transactions')?.select('*, orders(order_number, pickup_address, dropoff_address)')?.eq('user_id', userId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data?.map(t => ({
      id: t?.id,
      orderId: t?.order_id,
      orderNumber: t?.orders?.order_number,
      type: t?.transaction_type,
      status: t?.status,
      amount: t?.amount,
      currency: t?.currency,
      paymentMethod: t?.payment_method,
      reference: t?.reference,
      description: t?.description,
      createdAt: t?.created_at,
    })) || [];
  },

  async getDriverTransactions(driverProfileId) {
    const { data, error } = await supabase?.from('transactions')?.select('*, orders(order_number)')?.eq('driver_id', driverProfileId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data?.map(t => ({
      id: t?.id,
      orderId: t?.order_id,
      orderNumber: t?.orders?.order_number,
      type: t?.transaction_type,
      status: t?.status,
      amount: t?.amount,
      currency: t?.currency,
      paymentMethod: t?.payment_method,
      reference: t?.reference,
      description: t?.description,
      createdAt: t?.created_at,
    })) || [];
  },

  async getAllTransactions(filters = {}) {
    let query = supabase?.from('transactions')?.select(`
        *,
        user_profiles(full_name, email),
        orders(order_number)
      `)?.order('created_at', { ascending: false });

    if (filters?.status) query = query?.eq('status', filters?.status);
    if (filters?.type) query = query?.eq('transaction_type', filters?.type);
    if (filters?.limit) query = query?.limit(filters?.limit);

    const { data, error } = await query;
    if (error) throw error;
    return data?.map(t => ({
      id: t?.id,
      orderId: t?.order_id,
      orderNumber: t?.orders?.order_number,
      userId: t?.user_id,
      userName: t?.user_profiles?.full_name,
      userEmail: t?.user_profiles?.email,
      type: t?.transaction_type,
      status: t?.status,
      amount: t?.amount,
      currency: t?.currency,
      paymentMethod: t?.payment_method,
      reference: t?.reference,
      description: t?.description,
      createdAt: t?.created_at,
    })) || [];
  },

  async getTransactionStats() {
    const { data, error } = await supabase?.from('transactions')?.select('amount, transaction_type, status, created_at');
    if (error) throw error;

    const completed = data?.filter(t => t?.status === 'completed') || [];
    const totalRevenue = completed?.filter(t => t?.transaction_type === 'payment')?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0);
    const totalPayouts = completed?.filter(t => t?.transaction_type === 'payout')?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0);

    return {
      totalRevenue,
      totalPayouts,
      netRevenue: totalRevenue - totalPayouts,
      totalTransactions: data?.length || 0,
    };
  },
};

// ============================================================
// REALTIME SERVICE
// ============================================================
export const realtimeService = {
  subscribeToOrderUpdates(orderId, callback) {
    const channel = supabase?.channel(`order_${orderId}`)?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: `id=eq.${orderId}`,
      }, (payload) => callback(payload))?.subscribe();
    return channel;
  },

  subscribeToDriverLocation(driverId, callback) {
    const channel = supabase
      ?.channel(`driver_location_${driverId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'driver_locations',
        filter: `driver_id=eq.${driverId}`,
      }, (payload) => callback(payload))
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'driver_locations',
        filter: `driver_id=eq.${driverId}`,
      }, (payload) => callback(payload))
      ?.subscribe();
    return channel;
  },

  subscribeToNewOrders(callback) {
    const channel = supabase?.channel('new_orders')?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'orders',
      }, (payload) => callback(payload))?.subscribe();
    return channel;
  },

  subscribeToOrderStatusChanges(callback) {
    const channel = supabase?.channel('order_status_changes')?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'orders',
      }, (payload) => callback(payload))?.subscribe();
    return channel;
  },

  subscribeToDriverStatusChanges(callback) {
    const channel = supabase?.channel('driver_status_changes')?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'driver_profiles',
      }, (payload) => callback(payload))?.subscribe();
    return channel;
  },

  unsubscribe(channel) {
    if (channel) supabase?.removeChannel(channel);
  },
};

// ============================================================
// ARRIVAL / GEOFENCE SERVICE
// ============================================================
export const arrivalService = {
  async completeOrderOnArrival(orderId, driverProfileId) {
    const { data, error } = await supabase?.rpc('complete_order_on_arrival', {
      p_order_id: orderId,
      p_driver_profile_id: driverProfileId,
    });
    if (error) throw error;
    return data;
  },

  async insertArrivalAlert(orderId, orderNumber) {
    const { error } = await supabase?.from('system_alerts')?.insert({
      title: 'Driver Arrived at Destination',
      description: `Order ${orderNumber || orderId} completed. Driver arrived at destination.`,
      severity: 'info',
      category: 'operations',
      status: 'resolved',
      action_required: false,
    });
    if (error) console.warn('Failed to insert arrival alert:', error?.message);
  },
};

// ============================================================
// ADMIN STATS SERVICE
// ============================================================
export const adminStatsService = {
  async getDashboardStats() {
    const [ordersResult, driversResult, usersResult, transactionsResult] = await Promise.all([
      supabase?.from('orders')?.select('status, total_amount, created_at', { count: 'exact' }),
      supabase?.from('driver_profiles')?.select('status', { count: 'exact' }),
      supabase?.from('user_profiles')?.select('role, created_at', { count: 'exact' }),
      supabase?.from('transactions')?.select('amount, transaction_type, status'),
    ]);

    const orders = ordersResult?.data || [];
    const drivers = driversResult?.data || [];
    const users = usersResult?.data || [];
    const transactions = transactionsResult?.data || [];

    const activeOrders = orders?.filter(o => !['delivered', 'cancelled']?.includes(o?.status))?.length;
    const onlineDrivers = drivers?.filter(d => d?.status === 'online')?.length;
    const totalCustomers = users?.filter(u => u?.role === 'customer')?.length;
    const totalRevenue = transactions?.filter(t => t?.transaction_type === 'payment' && t?.status === 'completed')?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0);

    return {
      totalOrders: orders?.length,
      activeOrders,
      onlineDrivers,
      totalDrivers: drivers?.length,
      totalCustomers,
      totalRevenue,
    };
  },

  async getRecentOrders(limit = 10) {
    return orderService?.getAllOrders({ limit });
  },

  async getRecentTransactions(limit = 10) {
    return transactionService?.getAllTransactions({ limit });
  },
};

// ============================================================
// SMS NOTIFICATION SERVICE
// ============================================================
export const smsNotificationService = {
  /**
   * Send SMS for arrival in 5 minutes (called from driver app when ETA <= 5 min)
   */
  async sendArrivalSoonSms(orderId) {
    const { data, error } = await supabase?.functions?.invoke('send-sms', {
      body: {
        to: null, // resolved server-side via order lookup
        event: 'arrival_soon',
        orderId,
        data: { order_id: orderId }
      }
    });
    if (error) throw error;
    return data;
  },

  /**
   * Send payment reminder SMS for a specific order
   */
  async sendPaymentReminderSms(orderId) {
    const { data, error } = await supabase?.functions?.invoke('send-sms', {
      body: {
        to: null, // resolved server-side via order lookup
        event: 'payment_reminder',
        orderId,
        data: { order_id: orderId }
      }
    });
    if (error) throw error;
    return data;
  },

  /**
   * Send a custom SMS directly to a phone number
   * Used for manual admin notifications
   */
  async sendCustomSms(to, event, data = {}) {
    const { data: result, error } = await supabase?.functions?.invoke('send-sms', {
      body: { to, event, data }
    });
    if (error) throw error;
    return result;
  },
};

// ============================================================
// RENTAL SERVICE
// ============================================================
export const rentalService = {
  async getActiveVehicles(filters = {}) {
    let query = supabase?.from('vehicles')
      ?.select(`*, vehicle_pricing(*), vehicle_media(*)`)
      ?.eq('status', 'active')
      ?.order('created_at', { ascending: false });
    if (filters?.vehicleType) query = query?.eq('vehicle_type', filters?.vehicleType);
    if (filters?.rentalMode) query = query?.in('rental_mode', [filters?.rentalMode, 'both']);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getVehicle(vehicleId) {
    const { data, error } = await supabase?.from('vehicles')
      ?.select(`*, vehicle_pricing(*), vehicle_media(*), vehicle_availability(*)`)
      ?.eq('id', vehicleId)
      ?.single();
    if (error) throw error;
    return data;
  },

  async getSignedMediaUrl(storagePath) {
    const { data, error } = await supabase?.storage?.from('vehicle-media')?.createSignedUrl(storagePath, 3600);
    if (error) throw error;
    return data?.signedUrl;
  },

  async createBooking(params) {
    const { data, error } = await supabase?.rpc('create_rental_booking', {
      p_customer_id: params?.customerId,
      p_vehicle_id: params?.vehicleId,
      p_start_at: params?.startAt,
      p_end_at: params?.endAt,
      p_pickup_location: params?.pickupLocation,
      p_dropoff_location: params?.dropoffLocation,
      p_rental_mode: params?.rentalMode,
      p_extras: params?.extras,
      p_price_breakdown: params?.priceBreakdown,
    });
    if (error) throw error;
    return data;
  },

  async getCustomerBookings(customerId) {
    const { data, error } = await supabase?.from('rental_bookings')
      ?.select(`*, vehicles(title, make, model, year, vehicle_media(*))`)
      ?.eq('customer_id', customerId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getOwnerVehicles(ownerId) {
    const { data, error } = await supabase?.from('vehicles')
      ?.select(`*, vehicle_pricing(*), vehicle_media(*)`)
      ?.eq('owner_id', ownerId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getOwnerBookings(ownerId) {
    const { data, error } = await supabase?.from('rental_bookings')
      ?.select(`*, vehicles(title, make, model), user_profiles!rental_bookings_customer_id_fkey(full_name, phone, email)`)
      ?.eq('owner_id', ownerId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async ownerRespondToBooking(bookingId, ownerId, accept) {
    const { data, error } = await supabase?.rpc('owner_respond_to_booking', {
      p_booking_id: bookingId,
      p_owner_id: ownerId,
      p_accept: accept,
    });
    if (error) throw error;
    return data;
  },

  async uploadVehicleMedia(vehicleId, file, sortOrder = 0, isPrimary = false) {
    const path = `vehicles/${vehicleId}/${Date.now()}_${sortOrder}_${file?.name}`;
    const { data: uploadData, error: uploadError } = await supabase?.storage?.from('vehicle-media')?.upload(path, file);
    if (uploadError) throw uploadError;
    const { data, error } = await supabase?.from('vehicle_media')?.insert({
      vehicle_id: vehicleId,
      storage_path: uploadData?.path,
      sort_order: sortOrder,
      is_primary: isPrimary,
    })?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ============================================================
// CORPORATE LEASING SERVICE
// ============================================================
export const corporateLeasingService = {
  async getCorporateAccount(userId) {
    const { data, error } = await supabase?.from('corporate_accounts')
      ?.select('*')?.eq('admin_user_id', userId)?.maybeSingle();
    if (error) throw error;
    return data;
  },

  async createLeaseRequest(corporateId, requirements) {
    const { data, error } = await supabase?.from('corporate_lease_requests')?.insert({
      corporate_id: corporateId,
      requirements,
      req_status: 'submitted',
    })?.select()?.single();
    if (error) throw error;
    return data;
  },

  async getLeases(corporateId) {
    const { data, error } = await supabase?.from('corporate_leases')
      ?.select('*')?.eq('corporate_id', corporateId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getInvoices(corporateId) {
    const { data, error } = await supabase?.from('rental_invoices')
      ?.select('*')?.eq('corporate_id', corporateId)?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // Admin functions
  async getAllLeaseRequests() {
    const { data, error } = await supabase?.from('corporate_lease_requests')
      ?.select(`*, corporate_accounts(company_name, billing_email, contact_person)`)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async sendQuote(requestId, adminId, quoteDetails) {
    const { data, error } = await supabase?.rpc('create_corporate_lease_quote', {
      p_request_id: requestId,
      p_admin_id: adminId,
      p_quote_details: quoteDetails,
    });
    if (error) throw error;
    return data;
  },
};

// ============================================================
// ADMIN RENTAL SERVICE
// ============================================================
export const adminRentalService = {
  async getPendingVehicles() {
    const { data, error } = await supabase?.from('vehicles')
      ?.select(`*, vehicle_pricing(*), vehicle_media(*), user_profiles!vehicles_owner_id_fkey(full_name, email, phone)`)
      ?.eq('status', 'pending_review')
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async approveVehicle(vehicleId, adminId) {
    const { data, error } = await supabase?.rpc('approve_vehicle_listing', {
      p_vehicle_id: vehicleId,
      p_admin_id: adminId,
    });
    if (error) throw error;
    return data;
  },

  async rejectVehicle(vehicleId, adminId, reason) {
    const { data, error } = await supabase?.rpc('reject_vehicle_listing', {
      p_vehicle_id: vehicleId,
      p_admin_id: adminId,
      p_reason: reason,
    });
    if (error) throw error;
    return data;
  },

  async updatePricing(vehicleId, adminId, pricingData) {
    const { data, error } = await supabase?.rpc('update_vehicle_pricing', {
      p_vehicle_id: vehicleId,
      p_admin_id: adminId,
      ...pricingData,
    });
    if (error) throw error;
    return data;
  },

  async getAllRentalBookings(filters = {}) {
    let query = supabase?.from('rental_bookings')
      ?.select(`*, vehicles(title, make, model), user_profiles!rental_bookings_customer_id_fkey(full_name, email, phone)`)
      ?.order('created_at', { ascending: false });
    if (filters?.status) query = query?.eq('booking_status', filters?.status);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async updateBookingStatus(bookingId, adminId, newStatus, adminNotes) {
    const { data, error } = await supabase?.rpc('admin_update_booking_status', {
      p_booking_id: bookingId,
      p_admin_id: adminId,
      p_new_status: newStatus,
      p_admin_notes: adminNotes,
    });
    if (error) throw error;
    return data;
  },

  async releaseDeposit(bookingId, adminId, depositStatus, damageNotes) {
    const { data, error } = await supabase?.rpc('release_rental_deposit', {
      p_booking_id: bookingId,
      p_admin_id: adminId,
      p_deposit_status: depositStatus,
      p_damage_notes: damageNotes,
    });
    if (error) throw error;
    return data;
  },
};
